import queryClient from "./queryClient";

export const LOGIN_PATH = "/login";

// Expire every cookie the document can see. A cookie is only overwritable with
// the same name/path/domain pair it was set with, so try each path prefix of
// the current URL against the host and its parent domains.
const clearCookies = () => {
	const cookies = document.cookie ? document.cookie.split(";") : [];
	if (!cookies.length) {
		return;
	}

	const paths = ["/"];
	let current = "";
	window.location.pathname
		.split("/")
		.filter(Boolean)
		.forEach((segment) => {
			current += `/${segment}`;
			paths.push(current);
		});

	const hostParts = window.location.hostname.split(".");
	const domains = [undefined];
	for (let i = 0; i < hostParts.length - 1; i++) {
		const domain = hostParts.slice(i).join(".");
		domains.push(domain, `.${domain}`);
	}

	const expiry = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
	cookies.forEach((cookie) => {
		const name = cookie.split("=")[0].trim();
		if (!name) {
			return;
		}
		paths.forEach((path) => {
			domains.forEach((domain) => {
				document.cookie = domain
					? `${name}=; ${expiry}; path=${path}; domain=${domain}`
					: `${name}=; ${expiry}; path=${path}`;
			});
		});
	});
};

const clearCacheStorage = async () => {
	if (!window.caches) {
		return;
	}
	const keys = await window.caches.keys();
	await Promise.all(keys.map((key) => window.caches.delete(key)));
};

const clearIndexedDB = async () => {
	if (!window.indexedDB || typeof window.indexedDB.databases !== "function") {
		return;
	}
	const databases = await window.indexedDB.databases();
	await Promise.all(
		databases
			.filter((db) => db.name)
			.map(
				(db) =>
					new Promise((resolve) => {
						const request = window.indexedDB.deleteDatabase(db.name);
						request.onsuccess = resolve;
						request.onerror = resolve;
						request.onblocked = resolve;
					})
			)
	);
};

// Wipes everything this origin has persisted: credentials, preferences,
// in-memory query cache, cookies, Cache Storage and IndexedDB. Each step is
// best effort so one failure (private mode, storage disabled) cannot leave the
// rest of the data behind.
export const clearSiteData = async () => {
	try {
		window.localStorage.clear();
	} catch (e) {
		/* storage unavailable */
	}

	try {
		window.sessionStorage.clear();
	} catch (e) {
		/* storage unavailable */
	}

	try {
		clearCookies();
	} catch (e) {
		/* cookies unavailable */
	}

	try {
		queryClient.clear();
	} catch (e) {
		/* query cache already torn down */
	}

	await Promise.all([
		clearCacheStorage().catch(() => {}),
		clearIndexedDB().catch(() => {}),
	]);
};

let loggingOut = false;

// Clears the session and hard-navigates to the login page. The full page load
// (rather than a router push) guarantees no component keeps stale data in
// memory. Concurrent 401s only trigger one redirect.
export const logout = async () => {
	if (loggingOut) {
		return;
	}
	loggingOut = true;

	await clearSiteData();

	if (window.location.pathname !== LOGIN_PATH) {
		window.location.replace(LOGIN_PATH);
	} else {
		loggingOut = false;
	}
};
